import { ArrowLeft, Link2 } from "lucide-react";
import { Navigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArticleCard, Layout, SEOHead } from "@/components";
import { FaFacebook, FaLinkedin, FaTwitter } from "@/components/shared/icons";
import { Button } from "@/components/ui/button";
import { getArticleById, getRelatedArticles } from "@/data/articles";

const Article = () => {
	const { id } = useParams<{ id: string }>();
	const article = id ? getArticleById(id) : undefined;

	if (!article) {
		return <Navigate to="/404" replace />;
	}

	const relatedArticles = getRelatedArticles(article.id);

	// Generate SEO-friendly description from article content
	const metaDescription =
		article.subtitle.length > 150
			? `${article.subtitle.substring(0, 147)}...`
			: article.subtitle;

	// Convert date to ISO string for article meta tags
	const publishedTime = article.date
		? new Date(article.date).toISOString()
		: undefined;

	// Generate keywords from article tags and category
	const metaKeywords = [
		article.category.toLowerCase(),
		...article.tags.map((tag) => tag.toLowerCase()),
	].join(", ");

	const handleCopyLink = () => {
		navigator.clipboard.writeText(window.location.href);
		toast.success("Link copied to clipboard!");
	};

	return (
		<Layout>
			<SEOHead
				title={article.title}
				description={metaDescription}
				keywords={metaKeywords}
				canonicalUrl={`/article/${article.id}`}
				ogType="article"
				ogImage={article.image}
				article={{
					publishedTime,
					author: article.author.name,
					section: article.category,
					tags: article.tags,
				}}
				noIndex
			/>

			{/* Back Navigation */}
			<div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
				<a
					href="/"
					className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-primary transition-colors"
				>
					<ArrowLeft className="w-4 h-4" />
					Back to articles
				</a>
			</div>

			{/* Hero Image */}
			<div className="relative w-full h-100 md:h-120 lg:h-150 mb-12">
				<img
					src={article.image}
					alt={`Imagem destacada: ${article.title} - ${article.subtitle}`}
					className="w-full h-full object-cover"
				/>
				<div className="absolute inset-0 bg-linear-to-t from-background via-background/50 to-transparent" />
			</div>

			<article className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 -mt-32 relative z-10">
				{/* Article Header */}
				<div className="mb-12 animate-slide-up">
					<div className="flex items-center gap-3 mb-6">
						<span className="px-4 py-2 rounded-full text-sm font-medium bg-muted text-foreground">
							{article.category}
						</span>
						<span className="text-sm text-muted-foreground">
							{article.date}
						</span>
						<span className="text-sm text-muted-foreground">•</span>
						<span className="text-sm text-muted-foreground">
							{article.readTime} read
						</span>
					</div>

					<h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-4 leading-tight">
						{article.title}
					</h1>

					<p className="text-xl text-muted-foreground mb-8">
						{article.subtitle}
					</p>

					{/* Author Info */}
					<div className="flex items-center justify-between border-t border-b border-border py-6">
						<div className="flex items-center gap-4">
							<img
								src={article.author.avatar}
								alt={`Foto do autor ${article.author.name} - ${article.author.bio}`}
								className="w-14 h-14 rounded-full object-cover"
							/>
							<div>
								<p className="font-semibold">{article.author.name}</p>
								<p className="text-sm text-muted-foreground">
									{article.author.bio}
								</p>
							</div>
						</div>

						{/* Share Buttons */}
						<div className="hidden md:flex items-center gap-2">
							<button
								type="button"
								onClick={handleCopyLink}
								className="w-10 h-10 rounded-full border border-border hover:border-primary hover:bg-muted transition-all flex items-center justify-center"
								aria-label="Copy link"
							>
								<Link2 className="w-4 h-4" />
							</button>
							<a
								href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(article.title)}&url=${encodeURIComponent(window.location.href)}`}
								target="_blank"
								rel="noopener noreferrer"
								className="w-10 h-10 rounded-full border border-border hover:border-primary hover:bg-muted transition-all flex items-center justify-center"
								aria-label="Share on Twitter"
							>
								<FaTwitter className="w-4 h-4" />
							</a>
							<a
								href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(window.location.href)}`}
								target="_blank"
								rel="noopener noreferrer"
								className="w-10 h-10 rounded-full border border-border hover:border-primary hover:bg-muted transition-all flex items-center justify-center"
								aria-label="Share on Facebook"
							>
								<FaFacebook className="w-4 h-4" />
							</a>
							<a
								href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(window.location.href)}`}
								target="_blank"
								rel="noopener noreferrer"
								className="w-10 h-10 rounded-full border border-border hover:border-primary hover:bg-muted transition-all flex items-center justify-center"
								aria-label="Share on LinkedIn"
							>
								<FaLinkedin className="w-4 h-4" />
							</a>
						</div>
					</div>
				</div>

				{/* Article Content */}
				<div className="prose prose-lg max-w-none mb-16 animate-slide-up stagger-2">
					<p className="text-lg leading-relaxed text-muted-foreground mb-8">
						{article.content.introduction}
					</p>

					{article.content.sections.map((section) => (
						<div key={section.content} className="mb-10">
							<h2 className="text-3xl font-bold mb-4">{section.heading}</h2>
							<p className="text-lg leading-relaxed text-muted-foreground">
								{section.content}
							</p>
						</div>
					))}

					<div className="mt-12 p-6 rounded-2xl bg-muted border-l-4 border-primary">
						<p className="text-lg leading-relaxed italic text-foreground">
							{article.content.conclusion}
						</p>
					</div>
				</div>

				{/* Tags */}
				<div className="mb-12 pb-12 border-b border-border">
					<div className="flex flex-wrap gap-3">
						{article.tags.map((tag) => (
							<span
								key={tag}
								className="px-4 py-2 rounded-full text-sm bg-muted text-foreground"
							>
								#{tag}
							</span>
						))}
					</div>
				</div>

				{/* Mobile Share Buttons */}
				<div className="md:hidden mb-12 pb-12 border-b border-border">
					<p className="text-sm font-semibold mb-4">Share this article</p>
					<div className="flex items-center gap-3">
						<button
							type="button"
							onClick={handleCopyLink}
							className="flex-1 py-3 rounded-full border border-border hover:border-primary hover:bg-muted transition-all flex items-center justify-center gap-2"
						>
							<Link2 className="w-4 h-4" />
							<span className="text-sm">Copy link</span>
						</button>
						<a
							href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(article.title)}&url=${encodeURIComponent(window.location.href)}`}
							target="_blank"
							rel="noopener noreferrer"
							className="w-12 h-12 rounded-full border border-border hover:border-primary hover:bg-muted transition-all flex items-center justify-center"
							aria-label="Share on Twitter"
						>
							<FaTwitter className="w-4 h-4" />
						</a>
						<a
							href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(window.location.href)}`}
							target="_blank"
							rel="noopener noreferrer"
							className="w-12 h-12 rounded-full border border-border hover:border-primary hover:bg-muted transition-all flex items-center justify-center"
							aria-label="Share on Facebook"
						>
							<FaFacebook className="w-4 h-4" />
						</a>
					</div>
				</div>

				{/* Newsletter CTA */}
				<div className="mb-16 rounded-2xl bg-card p-8 md:p-12 text-center">
					<h3 className="text-2xl md:text-3xl font-bold mb-4">
						Enjoyed this article?
					</h3>
					<p className="text-muted-foreground mb-6">
						Subscribe to receive more insights like this directly in your inbox.
					</p>
					<div className="flex flex-col sm:flex-row gap-4 max-w-md mx-auto">
						<input
							type="email"
							placeholder="Your email"
							className="flex-1 px-4 py-3 rounded-full border border-input bg-background focus:outline-hidden focus:ring-2 focus:ring-ring"
						/>
						<Button className="bg-primary hover:bg-primary/90 text-primary-foreground rounded-full px-8">
							Subscribe
						</Button>
					</div>
				</div>
			</article>

			{/* Related Articles */}
			<section className="bg-muted py-16 animate-fade-in">
				<div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
					<h2 className="text-3xl font-bold mb-8 animate-slide-up">
						You might also like
					</h2>
					<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
						{relatedArticles.map((relatedArticle, index) => (
							<div
								key={relatedArticle.id}
								className={`animate-slide-up stagger-${Math.min(index + 1, 3)}`}
							>
								<ArticleCard {...relatedArticle} size="small" />
							</div>
						))}
					</div>
				</div>
			</section>
		</Layout>
	);
};

export default Article;
